// Appointment status codes (Xano `appointment.status`) — the SINGLE SOURCE for
// every label, colour, accent and picker option in this app. Do not re-declare a
// status map anywhere else; import from here (BookingCalendar, AppointmentPrint,
// huddle.js and PatientAppointments each carried their own copy until 2026-09-10
// and they had already drifted apart).
//
// The scheme, as Ram set it out 2026-09-10 — read the asterisk carefully:
//
//   C  / C*   ยืนยันแล้ว       the booking stands. ★ = the PATIENT confirmed it
//                             themselves (portal / SMS) — not staff on the phone.
//                             ⚠️ RELABELLED 2026-09-13, from "มีนัดแล้ว". The MEANING
//                             did not change; the word collided. `patient.status`
//                             has its own value "มีนัดแล้ว", and the appointment
//                             drawer shows both six inches apart — the header pill
//                             (the patient) and the สถานะ field (this booking) — so
//                             two different things read identically. ยืนยันแล้ว is
//                             what the calendar's C/C* filter has always called
//                             them, so the app now agrees with itself.
//                             DO NOT rename `patient.status`'s มีนัดแล้ว to match:
//                             that string is a live enum value on table 19 and is
//                             compared by api 166, trigger 2 and finishSave().
//   P  / P*   เลื่อนนัด         postponed. ★ = the patient asked for it through
//                             the portal / SMS, rather than staff moving it.
//   R48       จองไว้ 48 ชม.     the clinic-invented 48h hold — see its section below.
//   W         รอติดต่อ          contactable, but has not picked up yet. A TO-DO:
//                             keep calling.
//   U         ติดต่อไม่ได้       no usable number, wrong/dead number, or repeated
//                             failed attempts. A DEAD END, not a to-do.
//   X         ยกเลิก / ไม่มาตามนัด — one code, both meanings: a slot the patient
//                             did not use, however that came about.
//
// ⚠️ THE ASTERISK CARRIES MEANING, and it always did — this file simply said
// otherwise ("nothing in this workspace gives the asterisk a distinct meaning")
// until 2026-09-10. The patient portal has been writing it all along:
// `patient_portal/functions/api/appointment-confirm.js` sets C* when a patient
// confirms, `appointment-change.js` sets P* when they ask to move the booking.
// (Both are deployed; ★ rows stay rare only because Xano task 3 still texts the
// old Bubble link — see docs/STATE.md.) So ★ is a status the SYSTEM earns on the
// patient's behalf, which is exactly why the picker below does not offer it: it
// must never be something the front desk types in for them.
//
// 🚨 W CHANGED MEANING on 2026-09-10 — it used to be **Walk-in**, and 3,425 rows
// still hold it from that era. Those rows now READ as รอติดต่อ; no backfill was
// run (Ram's call — see docs/STATE.md). The loss is small: walk-in as a *source*
// lives in `appointment.from`, which has its own 'Walk-in' value, so the status
// was duplicating a field that already says it better. U moved from ยังไม่ยืนยัน
// to ติดต่อไม่ได้ over 5,289 rows, which is close to compatible — U was already
// the code for "staff phoned an R48 patient and could not reach them".
//
// ✅ No Xano schema change was needed for any of this: table 20's enum is already
// exactly ["C","W","P","U","X","C*","P*","R48"] and every code above is in it.
//
// `hint` is the one-line meaning staff see in the status legend and in the
// picker's menu. It is Ram's scheme above, written out — not new policy.
export const APPT_STATUS = {
  C:    { label: 'ยืนยันแล้ว',   abbr: 'ยืนยัน', tone: 'green',
          hint: 'นัดนี้ยืนยันแล้ว — พนักงานบันทึกไว้' },
  'C*': { label: 'ยืนยันแล้ว (คนไข้ยืนยันเอง)', abbr: 'ยืนยัน★', tone: 'green',
          hint: 'คนไข้ยืนยันเองผ่าน SMS หรือ Patient Portal' },
  // ⚠️ D and N are NOT in the Xano enum. Measured 2026-09-10: 0 rows hold D and
  // 0 hold N, out of 823,627. They are kept here ONLY so an unexpected code still
  // renders with a label instead of raw, and they must NEVER be offered for
  // WRITING — APPT_STATUS_OPTIONS below is the write side and holds neither.
  // ⚠️ No-show is NOT missing despite N being unstorable: **X covers both ยกเลิก
  // and ไม่มาตามนัด** (Ram, 2026-09-10). So the closing SOP's No-show requirement
  // (SOP_front_counter.md:729) is satisfied by X, and N is redundant, not merely
  // unstorable. Do NOT add N to the Xano enum to "fix" this.
  D:    { label: 'เสร็จสิ้น',    abbr: 'เสร็จ',  tone: 'sky' },
  // ⚠️ P is เลื่อนนัด — POSTPONED, not "awaiting confirmation". Confirmed by Ram
  // 2026-09-09 after the wrong reading was briefly shipped into the Xano rule for
  // มีนัดแล้ว (api 166). A postponed booking means the patient does NOT have an
  // appointment: they cannot be marked มีนัดแล้ว on the strength of a P row, and
  // the huddle counts them under ต้องยืนยัน (isConfirmedStatus is C/C* only).
  // Blue, not amber — Ram, 2026-09-19. The block colour (#F2F9FF) was already a
  // pale blue, so pill and block now agree.
  P:    { label: 'เลื่อนนัด',    abbr: 'เลื่อน', tone: 'blue',
          hint: 'เลื่อนแล้ว — ถือว่าคนไข้ไม่มีนัด' },
  'P*': { label: 'เลื่อนนัด (คนไข้แจ้งเอง)', abbr: 'เลื่อน★', tone: 'blue',
          hint: 'คนไข้แจ้งเลื่อนเองผ่าน SMS หรือ Patient Portal' },
  // Purple, not amber — Ram, 2026-09-10. R48 shared amber with P (เลื่อนนัด), which
  // made a held slot and a postponed one look the same on a calendar. They are
  // opposites: one is a slot being kept, the other a slot being given up.
  R48:  { label: 'จองไว้ 48 ชม.', abbr: 'จอง48', tone: 'purple',
          hint: 'กันคิวไว้ 48 ชม. รอคนไข้ติดต่อกลับมายืนยัน' },
  // X = ยกเลิก AND ไม่มาตามนัด. The pill stays short; the picker spells both out.
  X:    { label: 'ยกเลิก',       abbr: 'ยกเลิก', tone: 'rose',
          hint: 'ยกเลิก หรือ ไม่มาตามนัด — คิวนี้ไม่ได้ใช้' },
  N:    { label: 'ไม่มาตามนัด',  abbr: 'ไม่มา',  tone: 'slate' },
  // W and U are the two halves of "not confirmed yet", and the split is the whole
  // point: W is work still to do (ring them again), U is work that has run out of
  // road. Yellow vs orange (U was slate until Ram, 2026-09-19) — and yellow is the
  // colour the calendar block already gets from functions/api/appointment-status/[id].js
  // (#FEFDDD), so pill and block agree. U's block moved to orange the same day.
  W:    { label: 'รอติดต่อ',      abbr: 'รอติดต่อ', tone: 'yellow',
          hint: 'ยังไม่ได้รับสาย — ยังต้องโทรต่อ' },
  U:    { label: 'ติดต่อไม่ได้',   abbr: 'ติดต่อไม่ได้', tone: 'orange',
          hint: 'เบอร์ผิด / ไม่มีเบอร์ / โทรหลายครั้งไม่ติด — จบทาง' },
}

// tone → { pill, dot, accent }. `accent` is the vivid bar/marker colour; the pill
// is the pale badge. (The old `violet` tone died with W = Walk-in — it existed
// only for W, and it sat one hue away from R48's purple.)
export const APPT_STATUS_TONES = {
  green:  { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', accent: '#10b981' },
  sky:    { pill: 'bg-sky-50 text-sky-700 ring-sky-200',             dot: 'bg-sky-500',     accent: '#0ea5e9' },
  blue:   { pill: 'bg-blue-50 text-blue-700 ring-blue-200',          dot: 'bg-blue-500',    accent: '#3b82f6' },
  orange: { pill: 'bg-orange-50 text-orange-700 ring-orange-200',    dot: 'bg-orange-500',  accent: '#f97316' },
  amber:  { pill: 'bg-amber-50 text-amber-700 ring-amber-200',       dot: 'bg-amber-500',   accent: '#f59e0b' },
  rose:   { pill: 'bg-rose-50 text-rose-700 ring-rose-200',          dot: 'bg-rose-500',    accent: '#ef4444' },
  yellow: { pill: 'bg-yellow-50 text-yellow-800 ring-yellow-200',    dot: 'bg-yellow-400',  accent: '#eab308' },
  purple: { pill: 'bg-purple-50 text-purple-700 ring-purple-200',    dot: 'bg-purple-500',  accent: '#a855f7' },
  slate:  { pill: 'bg-slate-100 text-slate-600 ring-slate-200',      dot: 'bg-slate-400',   accent: '#94a3b8' },
  none:   { pill: 'bg-slate-100 text-slate-500 ring-slate-200',      dot: 'bg-slate-400',   accent: '#cbd5e1' },
}

// code → { label, abbr, hint, pill, dot, accent }; unknown/empty codes degrade
// to a neutral pill rather than showing staff a bare letter.
export function apptStatusInfo(code) {
  const c = String(code ?? '').trim()
  const hit = APPT_STATUS[c]
  const tone = APPT_STATUS_TONES[hit?.tone] || APPT_STATUS_TONES.none
  return { label: hit?.label || (c || '—'), abbr: hit?.abbr || (c || '—'), hint: hit?.hint || '', ...tone }
}

// code → Thai label, for the many `title={STATUS_TH[s] || s}` tooltips.
export const STATUS_TH = Object.fromEntries(
  Object.entries(APPT_STATUS).map(([code, s]) => [code, s.label]),
)
// code → badge classes; an unknown code lands on the neutral `none` pill.
export const codeCls = (s) => apptStatusInfo(s).pill
// code → vivid accent hex (the left bar on a card).
export const accentOf = (s) => apptStatusInfo(s).accent
// A lapsed R48 greys out — the hold is over, the slot is fair game again.
export const R48_EXPIRED_CLS = 'bg-slate-100 text-slate-500 ring-slate-200'

/* -------------------------------------------------------------------------- */
/* The WRITE side — what staff may set from the status picker                  */
/* -------------------------------------------------------------------------- */
/**
 * ⚠️ Every code here MUST exist in table 20's status enum
 * (["C","W","P","U","X","C*","P*","R48"]) or the write is rejected and the field
 * lies to the front desk. D (เสร็จสิ้น) and N (ไม่มาตามนัด) were offered here until
 * 2026-09-10 and could never save — a census found 0 rows holding either.
 *
 * C* and P* are deliberately ABSENT: the asterisk means the patient confirmed
 * through the portal/SMS, so it is something the system earns, not something the
 * front desk types. If Ram ever wants staff to be able to record "the patient
 * replied to the SMS themselves", add them here — not before.
 *
 * ⚠️ Do NOT render this list directly in a picker — use apptStatusOptionsFor(),
 * which adds back whatever code the row actually holds. A row's own status
 * missing from its own picker is the bug documented there.
 *
 * The letter is shown next to each label because staff read the raw code on the
 * calendar badge, and the codes are how Ram specifies the workflow.
 */
export const APPT_STATUS_OPTIONS = [
  { code: '',    label: '— ไม่ระบุ —' },
  { code: 'C',   label: 'ยืนยันแล้ว (C)' },
  { code: 'P',   label: 'เลื่อนนัด (P)' },
  { code: 'R48', label: 'จองไว้ 48 ชม. (R48)' },
  { code: 'W',   label: 'รอติดต่อ (W)' },
  { code: 'U',   label: 'ติดต่อไม่ได้ (U)' },
  { code: 'X',   label: 'ยกเลิก / ไม่มาตามนัด (X)' },
]

/**
 * The ★ codes: statuses the SYSTEM earns on the patient's behalf. The patient
 * portal writes them (appointment-confirm.js → C*, appointment-change.js → P*)
 * and NOTHING in the CRM may assign one — "the patient confirmed it themselves"
 * is a fact about the patient, so a member of staff typing it in would be
 * fabricating it.
 */
export const SYSTEM_APPT_STATUSES = ['C*', 'P*']
export const isSystemApptStatus = (code) => SYSTEM_APPT_STATUSES.includes(String(code ?? '').trim())

// Menu label for a status that is shown but not assignable. Built by hand
// because APPT_STATUS's labels already carry their own parentheses.
const LOCKED_OPTION_LABEL = {
  'C*': 'ยืนยันแล้ว · คนไข้ยืนยันเอง (C*)',
  'P*': 'เลื่อนนัด · คนไข้แจ้งเอง (P*)',
}

/**
 * The picker options for ONE row, given the status it is currently SAVED with.
 *
 * 🚨 READABLE ≠ WRITABLE, and conflating the two is what broke this. The picker
 * used to offer only APPT_STATUS_OPTIONS, so a C* row matched no option at all —
 * and React, when a <select>'s value matches nothing, quietly selects the first
 * non-disabled <option>. A patient-confirmed booking therefore displayed
 * "— ไม่ระบุ —" to the front desk, and the first touch of that dropdown replaced
 * the ★ with whatever was picked, with no way back. (Found on appointment
 * #826447, 2026-09-12.)
 *
 * So: the row's own status is ALWAYS among its options, flagged `locked` when it
 * is not otherwise writable. `locked` options are clickable only as THEMSELVES —
 * an undo for a mis-click, never an assignment, because an option for a code the
 * row does not hold is never generated in the first place.
 *
 * Also covers a code the enum lost or never had (D/N — 0 rows, but a row holding
 * one would otherwise be re-labelled by merely opening the drawer).
 */
export function apptStatusOptionsFor(current) {
  const c = String(current ?? '').trim()
  if (!c || APPT_STATUS_OPTIONS.some((o) => o.code === c)) return APPT_STATUS_OPTIONS
  const label = LOCKED_OPTION_LABEL[c] || `${APPT_STATUS[c]?.label || c} (${c})`
  return [{ code: c, label, locked: true }, ...APPT_STATUS_OPTIONS]
}

/**
 * May staff move this row from `original` to `next`? The belt to the picker's
 * brace — call it before writing, so the rule holds even if a future UI (or a
 * stale draft) offers something it shouldn't.
 *
 * Putting the status back where it started is always allowed: it writes nothing,
 * because ApptEditDrawer#save only POSTs a status that actually differs.
 */
export function canSetApptStatus(next, original) {
  const n = String(next ?? '').trim()
  if (n === String(original ?? '').trim()) return true
  return APPT_STATUS_OPTIONS.some((o) => o.code === n)
}

/**
 * Every status staff can meet on a calendar, in the order the legend shows them
 * — the writable set with each ★ sibling beside its plain code. D and N are left
 * out: no row holds either (see above), so a legend entry would invent a state.
 */
// ('R48' as a literal, not the `R48` const — that is declared further down this
// file and this array is evaluated at import time.)
export const APPT_STATUS_LEGEND = ['C', 'C*', 'P', 'P*', 'R48', 'W', 'U', 'X']

/* -------------------------------------------------------------------------- */
/* R48 — "Reserve for 48h"                                                     */
/* -------------------------------------------------------------------------- */
/**
 * R48 is a clinic-invented status for the patient who is not ready to commit to
 * a next appointment. Rather than let them walk out with nothing (a patient with
 * no next appointment is a patient we lose), staff hold the slot for 48 hours and
 * tell them to call back to confirm. Confirm → C. Silence → the row just stays
 * R48 and lapses.
 *
 * The 48h window is what gives the code its meaning, so R48 is really TWO states:
 *
 *   within 48h  → treat as a confirmed booking. The slot is genuinely held; if
 *                 another patient wants it, staff must ring the R48 patient first
 *                 and get a yes/no before giving it away.
 *   past 48h    → the hold has lapsed, the slot is fair game, no call required.
 *
 * Expiry is DERIVED at render time, never written back — nothing auto-mutates the
 * row, so the status stays R48 in Xano forever and the UI just greys it out.
 *
 * ⚠️ Exception worth knowing about: the ยืนยันแล้ว calendar filter hides R48
 * outright, even inside the 48h window. "Treated as confirmed" is about slot
 * contention (see above), not about the filter — staff asked for that filter to
 * mean strictly-C so it stays a clean list of true confirmations.
 */
export const R48 = 'R48'
export const R48_WINDOW_MS = 48 * 60 * 60 * 1000

export const isR48 = (code) => String(code ?? '').trim() === R48

/**
 * When the 48h clock started. Prefers the explicit `R48_at` stamp written the
 * moment staff set the status (see ApptEditDrawer#save); falls back to the row's
 * `created_at` for rows predating that field — correct for the common case where
 * R48 was chosen at creation time, and the only thing available for the rest.
 *
 * ⚠️ `R48_at` has a CAPITAL R — that is the real column name on table 20 (added
 * 2026-09-09). Xano field names are case-sensitive: the lowercase `r48_at` this
 * code used until then was silently dropped on every write, which is why the
 * clock always fell back to `created_at`.
 */
export function r48StampOf(a) {
  const raw = a?.R48_at ?? a?.created_at
  if (raw == null || raw === '') return null
  const ms = typeof raw === 'number' ? raw : Date.parse(raw)
  return Number.isFinite(ms) ? ms : null
}

/**
 * R48 state for one appointment row.
 * → { is, expired, msLeft, hoursLeft, hint }
 * A missing/unparseable stamp is treated as expired: we cannot prove the hold is
 * still live, and wrongly holding a slot costs more than wrongly releasing one.
 */
export function r48State(a, now = Date.now()) {
  if (!isR48(a?.status)) return { is: false, expired: false, msLeft: 0, hoursLeft: 0, hint: '' }
  const start = r48StampOf(a)
  if (start == null) return { is: true, expired: true, msLeft: 0, hoursLeft: 0, hint: 'หมดเวลา' }
  const msLeft = start + R48_WINDOW_MS - now
  if (msLeft <= 0) return { is: true, expired: true, msLeft: 0, hoursLeft: 0, hint: 'หมดเวลา' }
  const hoursLeft = Math.ceil(msLeft / 3600000)
  return { is: true, expired: false, msLeft, hoursLeft, hint: `เหลือ ${hoursLeft} ชม.` }
}

/**
 * The status a row should be JUDGED as, once the R48 window is resolved:
 * live R48 → 'C' (the slot is held), lapsed R48 → 'W' (รอติดต่อ — the hold is
 * over and the clinic now owes this patient a call). Every other code passes
 * through untouched. Use for slot-contention / tallies — NOT for rendering (the
 * badge always shows the real R48 code).
 *
 * ⚠️ A lapsed hold used to resolve to 'U'. That was right while U meant
 * ยังไม่ยืนยัน; it is wrong now that U means ติดต่อไม่ได้, because nobody has
 * necessarily tried to ring this patient at all — the 48h simply ran out. W is
 * the honest answer. Nothing renders this value, so the change is a rename of an
 * internal verdict, not a UI change.
 */
export function effectiveStatus(a, now = Date.now()) {
  const s = String(a?.status ?? '').trim()
  if (!isR48(s)) return s
  return r48State(a, now).expired ? 'W' : 'C'
}

/* -------------------------------------------------------------------------- */
/* "Does this patient still have an appointment?"                              */
/* -------------------------------------------------------------------------- */
/**
 * The appointment statuses that COUNT as the patient having a booking.
 *
 * 🚨 THIS LIST IS A MIRROR, NOT A DECISION. The same rule is written twice on the
 * server and must stay identical in all three places:
 *   • Xano api 166  POST /patient/status/{id} — refuses "มีนัดแล้ว" without one
 *   • Xano table trigger 2 "patient status" (table 20) — demotes to "Error" when
 *     the last one goes away (rewritten 2026-09-10 to match api 166; before that
 *     it had NO status filter and a postponed row still counted)
 *   • here, so the CRM can ask staff for a new status at the moment they cause it
 * Change one and you MUST change the other two, or the browser and the server
 * will disagree about who has an appointment.
 *
 * NULL and "" both count: an appointment booked more than a day or two out
 * normally carries no status at all — staff work C/P through for tomorrow only.
 * They are DIFFERENT values in Postgres and the live table holds both (52,427
 * NULL, 14,765 ""), which is exactly the bug that made "มีนัดแล้ว" impossible to
 * set for most patients until 2026-09-09.
 *
 * P/P* (เลื่อนนัด), X (ยกเลิก), N, D, W and U do NOT count.
 *
 * ⚠️ OPEN QUESTION as of 2026-09-10, deliberately left alone. Under the new
 * scheme W (รอติดต่อ) and U (ติดต่อไม่ได้) describe how the CONFIRMATION CALL went,
 * not whether the booking exists — the slot is still on the calendar in both
 * cases. So an argument exists for counting them, the same way ""/NULL counts.
 * Changing it means editing Xano api 166 AND table trigger 2 on live production
 * data, which is Ram's call, not a side effect of a labelling change.
 *
 * ⚠️ R48 counts whether or not its 48h window has LAPSED — deliberately NOT
 * `effectiveStatus()`, which resolves a lapsed hold to 'W'. The window governs
 * whether the SLOT may be given away, not whether the patient has an appointment.
 * Using effectiveStatus here would make the browser prompt for a new status while
 * the server quietly kept มีนัดแล้ว.
 */
export const BOOKING_STATUSES = ['', 'C', 'C*', R48]

/**
 * One row: is this a live booking that counts?
 * `active === false` is the soft-delete (ลบนัดหมาย) — note that CANCELLING is
 * `status: 'X'` and leaves `active` true, so both tests are needed.
 */
export function countsAsBooking(a) {
  if (!a || a.active === false) return false
  return BOOKING_STATUSES.includes(String(a.status ?? '').trim())
}

/**
 * Does this patient still have a future appointment that counts?
 * `rows` is whatever GET /api/appointments returned for them.
 */
export function hasFutureBooking(rows, now = Date.now()) {
  return (Array.isArray(rows) ? rows : []).some((a) => {
    if (!countsAsBooking(a)) return false
    const raw = a?.start_date
    if (raw == null || raw === '') return false
    const ms = typeof raw === 'number' ? raw
      : /^\d+$/.test(String(raw)) ? Number(raw)
      : Date.parse(raw)
    return Number.isFinite(ms) && ms >= now
  })
}
